import cn from 'classnames';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useAuthModal } from '@features/common/protected-auth-route/model.ts';
import { useExerciseLoader } from '@features/exercises/exercise-form/model/useExerciseUpload.ts';
import { useProgramModalBuyState } from '@features/programs/buy-program/model.ts';
import { BoomstreamPlayButton } from '@shared/assets/index.ts';
import { useAppDispatch, useUser } from '@shared/hooks';
import type { Video } from '@shared/types/common';
import type { ExerciseVideo } from '@shared/types/exercises';
import type { Program } from '@shared/types/programs';
import type { PromoVideo } from '@shared/types/promo-videos';
import { Loader } from '@src/library/components/common/index.ts';
import { openNotifyModal } from '@src/library/redux/modal/index.ts';
import * as st from './styles.module.scss';

const openFakeVideoNotify = () => {
  return openNotifyModal({
    title: 'Уведомление',
    text: 'Видео не найдено. Обратитесь в техническую поддержку.',
    confirmText: 'Хорошо',
  });
};

export type PlayerVideoType = ExerciseVideo | PromoVideo | Video | undefined;

export type PlayerProps = {
  video: PlayerVideoType;
  className?: string;
  isNeedPay?: boolean;
  isCurrentVideoAvailableBySchedule?: boolean;
  onCurrentVideoNotAvailableClick?: () => void;
  program?: Program;
};

export const Player: React.FC<PlayerProps> = ({
  video,
  className,
  isNeedPay,
  program,
  isCurrentVideoAvailableBySchedule = true,
  onCurrentVideoNotAvailableClick,
}) => {
  const { isAuth } = useUser();
  const dispatch = useAppDispatch();
  const { openAuth } = useAuthModal();
  const { doOpenModal } = useProgramModalBuyState(program?.pk);
  const { isLoading } = useExerciseLoader();

  const [isIframeLoading, setIsIframeLoading] = useState(true);

  const onFakeVideoClick = useCallback(() => {
    if (!isAuth) {
      return openAuth();
    } else if (isNeedPay) {
      doOpenModal();
    } else if (!isCurrentVideoAvailableBySchedule) {
      onCurrentVideoNotAvailableClick?.();
    } else {
      return dispatch(openFakeVideoNotify());
    }
  }, [
    dispatch,
    isAuth,
    doOpenModal,
    isNeedPay,
    openAuth,
    isCurrentVideoAvailableBySchedule,
    onCurrentVideoNotAvailableClick,
  ]);

  const randomValue = useMemo(() => {
    return Date.now() + Math.random() * 1000;
  }, [video]);

  const videoSrc = useMemo(() => {
    if (!video) return;
    setIsIframeLoading(true);
    switch (video.video_type) {
      case 'B':
        return `https://play.boomstream.com/${video.code}?title=0&size=cover&noCache=${randomValue}`;
      default:
        return video.embed_url;
    }
  }, [video, randomValue]);

  return (
    <div
      className={cn(st.container, className)}
      style={
        isCurrentVideoAvailableBySchedule && (isAuth || videoSrc)
          ? undefined
          : {
              backgroundImage: `url(${video?.screenshot_url})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              cursor: 'pointer',
            }
      }
      onClick={
        !isAuth || !videoSrc || !isCurrentVideoAvailableBySchedule ? onFakeVideoClick : undefined
      }
    >
      {videoSrc && isCurrentVideoAvailableBySchedule && (
        <>
          {isLoading ? (
            <Loader className={st.loader} />
          ) : (
            <>
              <iframe
                key={videoSrc}
                className={st.frame}
                src={`${videoSrc}`}
                allowFullScreen
                onLoad={() => setIsIframeLoading(false)}
              />
              {isIframeLoading && <Loader className={st.loader} />}
            </>
          )}
        </>
      )}
      {(!isAuth || !videoSrc || !isCurrentVideoAvailableBySchedule) && (
        <BoomstreamPlayButton className={st.boomstreamPlayButton} />
      )}
    </div>
  );
};
